import { GameError } from '../errors.ts';
import type { Fx, MinigameResult } from './types.ts';

/**
 * Real-time minigames run on a fixed step so the browser and the server compute the same thing.
 * The client plays the simulation live and records when its buttons change; the server replays
 * that trace from the session seed and scores it. A trace can only ever produce a result the rules allow.
 */
export const TICK_HZ = 60;
export const DT = 1 / TICK_HZ;
export const TICK_MS = 1000 / TICK_HZ;

/**
 * Input changes, ticks strictly increasing: `[tick, mask]` or, for games aimed with the pointer,
 * `[tick, mask, x, y]` in the game's own canvas units. Bit 0 is the main button; bits 1–3 are
 * engine-specific (lean left, lean right…). A short entry keeps the last pointer position.
 */
export type TraceEntry = [tick: number, mask: number] | [tick: number, mask: number, x: number, y: number];
export interface InputTrace {
  /** How many ticks the client ran. */
  ticks: number;
  changes: TraceEntry[];
}

/** Bits of the input mask. Engines document which they read. */
export const BTN = { main: 1, left: 2, right: 4, alt: 8 } as const;

export interface RealtimeSim {
  tick: number;
  /** Set once the attempt is over. */
  result: MinigameResult | null;
  fx: Fx[];
  /** One tick. `x`/`y` is the pointer, for engines that read it. */
  step(mask: number, x: number, y: number): void;
}

export const MAX_TRACE_CHANGES = 12000;
export const MAX_POINTER = 1000;

/**
 * Run a trace through a fresh simulation. If the trace stops before the attempt ends, the last
 * input is held until it does (or the engine's own time limit ends it).
 */
export function replay(sim: RealtimeSim, trace: InputTrace): { result: MinigameResult; ticks: number } {
  const { changes } = trace;
  if (changes.length > MAX_TRACE_CHANGES) throw new GameError('INVALID', 'That trace is too long.');
  for (let i = 0; i < changes.length; i++) {
    const [t, m, x, y] = changes[i]!;
    if (!Number.isInteger(t) || t < 0 || (i > 0 && t <= changes[i - 1]![0])) throw new GameError('INVALID', 'Trace ticks must increase.');
    if (!Number.isInteger(m) || m < 0 || m > 15) throw new GameError('INVALID', 'Bad input mask.');
    if (x !== undefined && !(Number.isInteger(x) && Number.isInteger(y) && x >= 0 && x <= MAX_POINTER && y! >= 0 && y! <= MAX_POINTER))
      throw new GameError('INVALID', 'Bad pointer position.');
  }
  let mask = 0, px = 0, py = 0;
  let next = 0;
  while (!sim.result) {
    while (next < changes.length && changes[next]![0] <= sim.tick) {
      const c = changes[next++]!;
      mask = c[1];
      if (c.length === 4) [px, py] = [c[2], c[3]];
    }
    sim.step(mask, px, py);
  }
  return { result: sim.result, ticks: sim.tick };
}

/**
 * Records a live player's input for `replay`: call `sample` once per tick with the current mask
 * (and pointer, rounded to whole canvas units, for engines that read it).
 */
export function traceRecorder() {
  const changes: TraceEntry[] = [];
  let mask = 0, px = 0, py = 0;
  return {
    sample(tick: number, m: number, x?: number, y?: number) {
      const rx = x === undefined ? px : Math.max(0, Math.min(MAX_POINTER, Math.round(x)));
      const ry = y === undefined ? py : Math.max(0, Math.min(MAX_POINTER, Math.round(y)));
      if (rx !== px || ry !== py) {
        changes.push([tick, m, rx, ry]);
        [mask, px, py] = [m, rx, ry];
      } else if (m !== mask) {
        changes.push([tick, m]);
        mask = m;
      }
    },
    trace: (ticks: number): InputTrace => ({ ticks, changes: [...changes] }),
  };
}

/** What a live sim sees each tick: the pointer, rounded the same way the recorder stores it. */
export const roundPointer = (v: number) => Math.max(0, Math.min(MAX_POINTER, Math.round(v)));
